// client/src/pages/School_Admin/Academics/TimetableTeachersTab.js
//
// SETUP · TEACHERS — the two facts a timetable cannot be generated without.
//
// Most secondary teachers here are part-time with their own days, and the
// system has never recorded either when they are on site or which classes they
// take a subject for. `teacher_subjects` says "Mr A can teach ICT"; with three
// ICT teachers that cannot say which of them has Form 3.
//
// The summary line on every row — "allocated 11 · can hold 12 · 2 days" — is
// the point of the screen. It catches over-commitment before anyone presses
// Generate, which is where most timetables in this country actually fail.

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, Divider, IconButton, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography, useTheme, Collapse, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const DAY_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * WHO TEACHES SOMETHING, account or not.
 *
 * The picker holds one list of values and a placeholder has no user id, so the
 * two are told apart by a prefix rather than by hoping their ids never collide
 * — which they would, since both start at 1.
 */
const keyOf = (t) => (t.userId != null ? `u:${t.userId}` : `p:${t.placeholderId}`);
const fromKey = (key) => (String(key).startsWith('u:')
  ? { userId: Number(String(key).slice(2)), placeholderId: null }
  : { userId: null, placeholderId: Number(String(key).slice(2)) });
const DAYS = [1, 2, 3, 4, 5, 6];

/** Minutes past midnight, or null. */
const toMin = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const toHHMM = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

const STEP = 30;

/**
 * The kinds a cell cycles through.
 *
 * `unavailable` exists so a hole can be cut out of an otherwise open day —
 * "in on Tuesday, but not before ten" — without describing the day twice.
 */
const KINDS = [null, 'available', 'preferred', 'unavailable'];
const nextKind = (k) => KINDS[(KINDS.indexOf(k ?? null) + 1) % KINDS.length];

const KIND_LABEL = {
  available: 'available',
  preferred: 'preferred',
  unavailable: 'not available',
};

function kindColour(theme, kind) {
  if (kind === 'available') return theme.palette.success.main;
  if (kind === 'preferred') return theme.palette.primary.main;
  if (kind === 'unavailable') return theme.palette.error.main;
  return 'transparent';
}

/* -------------------------------------------------------------------------- */

/** Stored windows -> a `${day}|${minutes}` -> kind map of half-hour cells. */
function cellsFromWindows(windows, rows) {
  const cells = {};
  for (const w of windows || []) {
    const start = toMin(w.startTime);
    const end = toMin(w.endTime);
    if (start == null || end == null) continue;
    for (const min of rows) {
      if (min >= start && min < end) cells[`${w.day}|${min}`] = w.kind || 'available';
    }
  }
  return cells;
}

/**
 * Cells -> the fewest windows that describe them.
 *
 * Adjacent half-hours of the same kind become one window, so a teacher who is
 * in all Tuesday morning is stored as one row rather than nine. The stored
 * shape is what the solver reads, and nine rows saying the same thing is nine
 * chances for one of them to be wrong.
 */
function windowsFromCells(cells, rows) {
  const out = [];
  for (const day of DAYS) {
    let run = null;
    for (const min of rows) {
      const kind = cells[`${day}|${min}`] || null;
      if (run && run.kind === kind && run.end === min) {
        run.end = min + STEP;
        continue;
      }
      if (run) { out.push(run); run = null; }
      if (kind) run = { day, kind, start: min, end: min + STEP };
    }
    if (run) out.push(run);
  }
  return out.map((w) => ({
    day: w.day, kind: w.kind, startTime: toHHMM(w.start), endTime: toHHMM(w.end),
  }));
}

/* -------------------------------------------------------------------------- */

/**
 * A teacher's week, painted.
 *
 * Click a cell to cycle it; drag to paint a run. Painting beats a form of
 * start/end pairs because a part-timer's week is a shape — "Tuesday and
 * Thursday mornings" — and a shape is faster to draw than to describe.
 */
function AvailabilityGrid({ windows, bounds, onChange }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const rows = useMemo(() => {
    const from = Math.floor((toMin(bounds?.min) ?? 7 * 60) / STEP) * STEP;
    const to = Math.ceil((toMin(bounds?.max) ?? 18 * 60) / STEP) * STEP;
    const out = [];
    for (let m = from; m < to; m += STEP) out.push(m);
    return out;
  }, [bounds]);

  const [cells, setCells] = useState(() => cellsFromWindows(windows, rows));
  // The kind being painted by the current drag. `undefined` means no drag is
  // in progress; `null` is a real brush that clears cells.
  const [brush, setBrush] = useState(undefined);

  useEffect(() => { setCells(cellsFromWindows(windows, rows)); }, [windows, rows]);

  const apply = useCallback((next) => {
    setCells(next);
    onChange(windowsFromCells(next, rows));
  }, [onChange, rows]);

  const paint = (day, min, kind) => {
    const key = `${day}|${min}`;
    const next = { ...cells };
    if (kind === null) delete next[key]; else next[key] = kind;
    apply(next);
  };

  // The whole grid stops painting on mouse-up anywhere, so releasing outside a
  // cell does not leave the brush stuck down.
  useEffect(() => {
    const stop = () => setBrush(undefined);
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const filled = Object.keys(cells).length;

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          {t('timetableGenerator.avail.hint', { defaultValue: 'Click a half hour to cycle it, or drag to paint.' })}
        </Typography>
        {['available', 'preferred', 'unavailable'].map((k) => (
          <Stack key={k} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: kindColour(theme, k) }} />
            <Typography variant="caption">{KIND_LABEL[k]}</Typography>
          </Stack>
        ))}
        {!filled && (
          <Chip size="small" color="warning" variant="outlined"
            label={t('timetableGenerator.avail.none', { defaultValue: 'nothing entered — every period assumed open' })} />
        )}
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `56px repeat(${DAYS.length}, minmax(56px, 1fr))`,
          gap: '2px',
          minWidth: 420,
          userSelect: 'none',
        }}>
          <Box />
          {DAYS.map((d) => (
            <Typography key={d} variant="caption" align="center" sx={{ fontWeight: 600 }}>
              {DAY_SHORT[d]}
            </Typography>
          ))}

          {rows.map((min) => (
            <React.Fragment key={min}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', pr: 0.5, lineHeight: '20px' }}>
                {min % 60 === 0 ? toHHMM(min) : ''}
              </Typography>
              {DAYS.map((d) => {
                const kind = cells[`${d}|${min}`] || null;
                return (
                  <Box
                    key={`${d}|${min}`}
                    onMouseDown={() => {
                      const k = nextKind(kind);
                      setBrush(k);
                      paint(d, min, k);
                    }}
                    onMouseEnter={(e) => {
                      // `buttons === 1` is the primary button still held. Without
                      // it the brush would follow the pointer with nothing
                      // pressed, and crossing the grid would repaint the week.
                      // `brush` is undefined when no drag is in progress; null is
                      // a real brush that clears.
                      if (e.buttons === 1 && brush !== undefined) paint(d, min, brush);
                    }}
                    sx={{
                      height: 20,
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      bgcolor: kind ? kindColour(theme, kind) : theme.palette.action.hover,
                      opacity: kind ? 0.85 : 1,
                      '&:hover': { outline: `1px solid ${theme.palette.text.secondary}` },
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */

/** One teacher: their caps, their week, and what they have been given. */
function TeacherRow({ teacher, bounds, onSaveCaps, onSaveAvailability, onDelete, busy }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [caps, setCaps] = useState(teacher.caps);
  const [windows, setWindows] = useState(teacher.availability);
  const [dirtyWindows, setDirtyWindows] = useState(false);

  useEffect(() => { setCaps(teacher.caps); }, [teacher.caps]);
  useEffect(() => { setWindows(teacher.availability); setDirtyWindows(false); }, [teacher.availability]);

  const over = teacher.load > teacher.capacity;

  // WHY THE CEILING IS WHERE IT IS. "6" and "6 because they only come in two
  // days" lead an admin to different fixes.
  const reason = {
    attend: 'the periods they attend',
    perDay: 'their per-day cap',
    perWeek: 'their weekly cap',
    daysOnSite: 'the days they come in',
  }[teacher.capacityReason] || 'their availability';

  const capsDirty = JSON.stringify(caps) !== JSON.stringify(teacher.caps);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">{teacher.name}</Typography>
            {/* SAID ON THE ROW, not only where they were created. A name in this
                list looks exactly like a member of staff, and a week built on
                guesses must never read as a staffed one. */}
            {teacher.noAccount && (
              <Chip size="small" variant="outlined" color="warning"
                label={t('timetableGenerator.teachers.noAccount', { defaultValue: 'no account · this year only' })} />
            )}
            {teacher.employmentType === 'PART_TIME' && (
              <Chip size="small" variant="outlined"
                label={t('timetableGenerator.teachers.partTime', { defaultValue: 'part-time' })} />
            )}
            {teacher.codes.map((c) => (
              <Chip key={c.subjectId} size="small" label={c.label} sx={{ height: 18, fontSize: 11 }} />
            ))}
          </Stack>
        </TableCell>
        <TableCell align="right">
          {/* THE LINE THAT CATCHES OVER-COMMITMENT — both numbers, always. */}
          <Tooltip title={`Ceiling set by ${reason}`}>
            <Chip
              size="small"
              color={over ? 'error' : (teacher.load ? 'success' : 'default')}
              variant={over ? 'filled' : 'outlined'}
              label={`${teacher.load} of ${teacher.capacity}`}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Typography variant="caption" color="text.secondary">
            {teacher.daysOnSite || 0} day{teacher.daysOnSite === 1 ? '' : 's'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            {!teacher.availabilityDeclared && (
              <Chip size="small" color="warning" variant="outlined"
                label={t('timetableGenerator.teachers.noAvail', { defaultValue: 'no availability' })} />
            )}
            {/* ONLY A NAME CAN BE REMOVED HERE. A member of staff belongs to the
                school, not to the timetable, and deleting them from a generator
                screen would be deleting an employee. */}
            {teacher.noAccount && onDelete && (
              <Tooltip title={t('timetableGenerator.teachers.removeHint', {
                defaultValue: 'Remove this name. Their days, their limits and everything '
                  + 'they were allocated go with them, and those subjects become vacancies.',
              })}
              >
                <span>
                  <IconButton
                    size="small" disabled={busy}
                    onClick={() => {
                      // ASKED, BECAUSE IT TAKES MORE THAN THE NAME. Whatever they
                      // were allocated goes with them, and the count is said out
                      // loud rather than discovered on the Allocation tab after.
                      const n = teacher.assignments.length;
                      const ok = window.confirm(t('timetableGenerator.teachers.removeConfirm', {
                        name: teacher.name,
                        count: n,
                        defaultValue: 'Remove {{name}}? Their days, their limits and the '
                          + '{{count}} subject(s) they were given all go with them, and those '
                          + 'subjects become vacancies.',
                      }));
                      if (ok) onDelete(teacher.placeholderId);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 2 }}>
              {over && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {teacher.name} is allocated {teacher.load} periods but can hold {teacher.capacity},
                  set by {reason} — over by {teacher.load - teacher.capacity}. No arrangement of the
                  week can fix this; either the allocation or the availability has to change.
                </Alert>
              )}

              <Typography variant="subtitle2" gutterBottom>
                {t('timetableGenerator.teachers.onSite', { defaultValue: 'When they are on site' })}
              </Typography>
              <AvailabilityGrid
                windows={windows}
                bounds={bounds}
                onChange={(w) => { setWindows(w); setDirtyWindows(true); }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small" variant="contained" disabled={!dirtyWindows || busy}
                  onClick={async () => { await onSaveAvailability(teacher.key, windows); }}
                >
                  {t('timetableGenerator.avail.save', { defaultValue: 'Save their week' })}
                </Button>
                {dirtyWindows && (
                  <Button size="small" onClick={() => { setWindows(teacher.availability); setDirtyWindows(false); }}>
                    Cancel
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                {t('timetableGenerator.teachers.limits', { defaultValue: 'What they can be asked to do' })}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {[
                  ['maxPeriodsPerWeek', 'Periods a week'],
                  ['maxPeriodsPerDay', 'Periods a day'],
                  ['maxConsecutivePeriods', 'In a row'],
                  ['maxDaysOnSite', 'Days on site'],
                ].map(([field, label]) => (
                  <TextField
                    key={field} size="small" label={label} type="number" sx={{ width: 130 }}
                    placeholder="no cap"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1, max: field === 'maxDaysOnSite' ? 6 : 60 }}
                    value={caps[field] ?? ''}
                    onChange={(e) => setCaps({
                      ...caps,
                      [field]: e.target.value === '' ? null : Number(e.target.value),
                    })}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('timetableGenerator.teachers.capsHint', { defaultValue: 'Blank means no cap. "Days on site" is the one part-timers care about — it keeps their lessons on as few days as the week allows.' })}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small" variant="contained" disabled={!capsDirty || busy}
                  onClick={() => onSaveCaps(teacher.key, caps)}
                >
                  {t('timetableGenerator.teachers.saveLimits', { defaultValue: 'Save their limits' })}
                </Button>
                {capsDirty && <Button size="small" onClick={() => setCaps(teacher.caps)}>Cancel</Button>}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                What they teach ({teacher.assignments.length})
              </Typography>
              {teacher.assignments.length ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {teacher.assignments.map((a) => (
                    <Chip
                      key={a.id} size="small" variant="outlined"
                      label={`${a.className} · ${a.subjectName}${a.periods ? ` · ${a.periods}p` : ''}`}
                      color={a.periods ? 'default' : 'warning'}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('timetableGenerator.teachers.noClasses', { defaultValue: 'Nothing yet. Allocate classes to them in the Allocation tab.' })}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('timetableGenerator.teachers.ceilingHint', { defaultValue: 'Their ceiling is counted from the grids of the classes they take, so it changes when their allocation does — a teacher with no classes has no ceiling to measure.' })}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function TeachersTab({
  data, bounds, busy, onSaveCaps, onSaveAvailability, onAddPlaceholder, onDeletePlaceholder,
}) {
  const { t } = useTranslation();
  if (!data) return <CircularProgress size={20} />;

  const teachers = data.teachers || [];
  const over = teachers.filter((t) => t.load > t.capacity);
  const undeclared = teachers.filter((t) => !t.availabilityDeclared);
  const named = teachers.filter((t) => t.noAccount);

  return (
    <Stack spacing={2}>
      {!teachers.length ? (
        <>
          <Alert severity="info">
            {t('timetableGenerator.teachers.none', { defaultValue: 'No secondary teaching staff found. Teachers, subject heads and class masters appear here.' })}
          </Alert>
          {/* OFFERED EVEN WHEN THE LIST IS EMPTY. A school with no accounts yet
              is exactly the school that needs to build a week out of names. */}
          <AddPlaceholder busy={busy} onAdd={onAddPlaceholder} />
        </>
      ) : (
        <>
      {/* CARRIED OVER, AND SAID SO. Settings an admin did not enter this
          year, shown with no explanation, are settings they will not trust —
          and they need to know that editing anything here makes this year's
          own copy, leaving last year's record intact. */}
      {data.carriedFrom && (
        <Alert severity="info">
          {t('timetableGenerator.carried', {
            defaultValue: 'This is last year\'s setup, shown because nothing has been entered for '
              + 'the current year yet. Change anything here and the whole set is copied into this '
              + 'year first, so last year\'s record is left exactly as it was.',
          })}
        </Alert>
      )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={t('timetableGenerator.teachers.count', { count: teachers.length, defaultValue: '{{count}} teachers' })} />
            <Chip size="small" color={over.length ? 'error' : 'default'}
              variant={over.length ? 'filled' : 'outlined'}
              label={t('timetableGenerator.teachers.over', { count: over.length, defaultValue: '{{count}} over-committed' })} />
            <Chip size="small" color={undeclared.length ? 'warning' : 'default'}
              variant="outlined"
              label={t('timetableGenerator.teachers.undeclared', { count: undeclared.length, defaultValue: '{{count}} without availability' })} />
            {!!named.length && (
              <Chip size="small" color="warning" variant="outlined"
                label={t('timetableGenerator.teachers.named', { count: named.length, defaultValue: '{{count}} named, no account' })} />
            )}
          </Stack>

          {/* WHAT A NAME IS AND HOW LONG IT LASTS. Placeholders sit in the same
              table as real staff so they can be given days and limits like
              anybody else — which makes it all the more important to say, once
              and plainly, that nobody behind these names can log in and that
              they do not follow the school into next year. */}
          {!!named.length && (
            <Alert severity="info">
              {t('timetableGenerator.teachers.namedHint', {
                count: named.length,
                defaultValue: '{{count}} of these are names only — nobody behind them has an account, '
                  + 'and they exist for this academic year alone. Give them days and limits here and '
                  + 'the generator will respect them exactly as it does a member of staff. Next year '
                  + 'starts without them, and anything they were allocated comes across as a vacancy.',
              })}
            </Alert>
          )}

          {!!undeclared.length && (
            <Alert severity="warning">
              {undeclared.length} of {teachers.length} teachers have no availability entered, so every
              period is assumed open for them. Generating now would be free to put a part-timer in on a
              day they have never attended.
            </Alert>
          )}

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('timetableGenerator.teachers.colTeacher', { defaultValue: 'Teacher' })}</TableCell>
                  <TableCell align="right">{t('timetableGenerator.teachers.colLoad', { defaultValue: 'Allocated / can hold' })}</TableCell>
                  <TableCell align="right">{t('timetableGenerator.teachers.colDays', { defaultValue: 'On site' })}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {teachers.map((teacher) => (
                  <TeacherRow
                    key={teacher.key}
                    teacher={teacher}
                    bounds={bounds}
                    busy={busy}
                    onSaveCaps={onSaveCaps}
                    onSaveAvailability={onSaveAvailability}
                    onDelete={onDeletePlaceholder}
                  />
                ))}
              </TableBody>
            </Table>
          </Paper>

          <AddPlaceholder busy={busy} onAdd={onAddPlaceholder} />
        </>
      )}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Name a teacher who is not in the system yet.
 *
 * A school plans next year in August with half its staff hired and none of them
 * logged in. It still knows Physics is split between two people; without a name
 * to put against the second, the whole subject went to whoever did have an
 * account and the periods could not be divided at all.
 *
 * A NAME, AND NOTHING ELSE. This used to sit on an allocation row and create the
 * person against that class-subject, which meant the same name typed on two rows
 * became two people — two weeks, two sets of limits, and no line anywhere
 * showing what one of them had actually been given. They are named here, beside
 * the staff, and offered for every class and every subject.
 */
function AddPlaceholder({ busy, onAdd }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const commit = () => {
    if (!name.trim()) return;
    onAdd({ name: name.trim() });
    setName('');
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        size="small" disabled={busy} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        onClick={() => setOpen(true)}
      >
        {t('timetableGenerator.teachers.addPlaceholder', { defaultValue: '+ a teacher with no account' })}
      </Button>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small" variant="standard" autoFocus disabled={busy} sx={{ minWidth: 220 }}
        placeholder={t('timetableGenerator.alloc.placeholderName', { defaultValue: 'Their name' })}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setName(''); setOpen(false); }
        }}
      />
      <Button size="small" disabled={busy || !name.trim()} onClick={commit}>
        {t('common.add', { defaultValue: 'Add' })}
      </Button>
      <Button size="small" onClick={() => { setName(''); setOpen(false); }}>
        {t('common.cancel', { defaultValue: 'Cancel' })}
      </Button>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * How one class-subject's periods divide between the teachers who share it.
 *
 * A TEACHER SHARING A SUBJECT SHOULD NOT TEACH ALL OF IT. Two names against
 * Mathematics used to mean the periods split evenly and nobody could say
 * otherwise; now the school says who takes how many, and the running total
 * refuses to add up wrong before it reaches the server.
 *
 * Blank is a real answer — "whatever is left" — so setting one share is enough
 * when there are two teachers.
 */
function ShareEditor({ row, busy, teacherName, onSave }) {
  const { t } = useTranslation();
  const promised = Number(row.periodsPerWeek) || 0;
  const current = (row.teachers || []).map((t) => ({
    userId: t.userId ?? null,
    placeholderId: t.placeholderId ?? null,
    periodsShare: t.periodsShare ?? null,
  }));
  // One lookup for both kinds of person, keyed on the identity rather than on a
  // user id that is null for half of them.
  const nameOf = (d) => teacherName.get(keyOf(d)) || '?';

  const [draft, setDraft] = useState(current);
  useEffect(() => { setDraft(current); }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, value) => setDraft(draft.map((d) => (keyOf(d) === key
    ? { ...d, periodsShare: value === '' ? null : Math.max(0, Number(value) || 0) }
    : d)));

  const given = draft.filter((d) => d.periodsShare != null);
  const total = given.reduce((n, d) => n + d.periodsShare, 0);
  const allSet = given.length === draft.length;
  const over = total > promised;
  const wrong = allSet && total !== promised;
  const dirty = JSON.stringify(draft) !== JSON.stringify(current);

  return (
    <Box sx={{ mt: 1 }}>
      <Stack spacing={0.5}>
        {draft.map((d) => (
          <Stack key={keyOf(d)} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small" type="number" variant="standard"
              disabled={busy}
              inputProps={{ min: 0, max: promised || 40, style: { width: 40, textAlign: 'right' } }}
              placeholder={t('timetableGenerator.alloc.rest', { defaultValue: 'rest' })}
              value={d.periodsShare ?? ''}
              onChange={(e) => set(keyOf(d), e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">{nameOf(d)}</Typography>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color={over || wrong ? 'error.main' : 'text.secondary'}>
          {t('timetableGenerator.alloc.shareTotal', {
            total, promised,
            defaultValue: '{{total}} of {{promised}} periods set',
          })}
          {!allSet && !over && t('timetableGenerator.alloc.shareRest', {
            defaultValue: ' · the rest is shared out',
          })}
        </Typography>
        {dirty && (
          <Button
            size="small" disabled={busy || over || wrong}
            onClick={() => onSave({ classId: row.classId, subjectId: row.subjectId, teachers: draft })}
          >
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * SETUP · ALLOCATION — who takes each class-subject.
 *
 * A LIST OF TEACHERS PER ROW, not one. Two teachers sharing a class's subject
 * is ordinary here, and it is the whole reason this is not a column on
 * `teacher_subjects`.
 */
export function AllocationTab({ data, busy, onSave }) {
  const { t } = useTranslation();
  const [classId, setClassId] = useState('');

  // Wrapped, because a fresh [] on every render makes every dependency below
  // change on every render.
  const classes = useMemo(() => data?.classes || [], [data]);
  // EVERYBODY, ACCOUNT OR NOT, ON EVERY ROW. A name is a person now rather than
  // a person-per-subject, so there is nothing left to filter them by — and the
  // one list keeps the picker and the Teachers tab showing the same people.
  const teachers = useMemo(() => data?.teachers || [], [data]);

  useEffect(() => {
    if (!classes.length) return;
    setClassId((held) => (classes.some((c) => String(c.id) === String(held)) ? held : classes[0].id));
  }, [classes]);

  const all = useMemo(
    () => (data?.classSubjects || []).filter((r) => String(r.classId) === String(classId)),
    [data, classId],
  );
  // ONLY WHAT IS TAUGHT. A subject with no periods never reaches the timetable,
  // so allocating a teacher to it records a duty that does not exist — and it
  // then shows in that teacher's list of classes and nowhere else.
  const rows = useMemo(() => all.filter((r) => Number(r.periodsPerWeek) > 0), [all]);
  const notTaught = useMemo(() => all.filter((r) => !(Number(r.periodsPerWeek) > 0)), [all]);

  // Keyed by identity, not by user id: `Number(null)` is 0, and one bucket for
  // every name in the school is how a chip ends up labelled with somebody else.
  const teacherName = useMemo(
    () => new Map(teachers.map((t) => [t.key, t.name])),
    [teachers],
  );

  if (!data) return <CircularProgress size={20} />;

  const unassigned = rows.filter((r) => !r.teacherUserIds.length && Number(r.periodsPerWeek) > 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Select size="small" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 240 }}>
          {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </Select>
        <Chip
          size="small"
          color={unassigned.length ? 'error' : 'success'}
          variant={unassigned.length ? 'filled' : 'outlined'}
          label={unassigned.length
            ? t('timetableGenerator.alloc.missing', { count: unassigned.length, defaultValue: '{{count}} with periods but no teacher' })
            : t('timetableGenerator.alloc.complete', { defaultValue: 'every subject with periods has a teacher' })}
        />
      </Stack>

      {!!notTaught.length && (
        <Alert severity="info">
          {t('timetableGenerator.alloc.notTaught', {
            count: notTaught.length,
            names: notTaught.map((r) => r.subjectName).sort().join(', '),
            defaultValue: '{{count}} subject(s) in this class have no periods this week, so they '
              + 'are not taught and need no teacher: {{names}}. Give them periods on the '
              + 'Subjects tab to schedule them.',
          })}
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('timetableGenerator.col.subject', { defaultValue: 'Subject' })}</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>{t('timetableGenerator.col.periods', { defaultValue: 'Periods/week' })}</TableCell>
              <TableCell>{t('timetableGenerator.alloc.taughtBy', { defaultValue: 'Taught by' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{row.subjectName}</Typography>
                    {row.subjectCode && <Chip size="small" label={row.subjectCode} sx={{ height: 18 }} />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {row.periodsPerWeek ?? (
                    <Typography variant="caption" color="text.secondary">
                      {t('timetableGenerator.alloc.notSet', { defaultValue: 'not set' })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    multiple size="small" fullWidth displayEmpty disabled={busy}
                    value={(row.teachers || []).map(keyOf)}
                    onChange={(e) => onSave({
                      classId: row.classId,
                      subjectId: row.subjectId,
                      // The picker changes WHO; everybody keeps the share they
                      // already had, and a new name arrives with none.
                      teachers: e.target.value.map((key) => ({
                        ...fromKey(key),
                        periodsShare: (row.teachers || [])
                          .find((t) => keyOf(t) === key)?.periodsShare ?? null,
                      })),
                    })}
                    renderValue={(picked) => (picked.length
                      ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {picked.map((key) => (
                            <Chip
                              key={key} size="small" sx={{ height: 20 }}
                              label={teacherName.get(key) || '?'}
                              // A name with no account is marked, so nobody
                              // mistakes the chart for a staffed week.
                              variant={String(key).startsWith('u:') ? 'filled' : 'outlined'}
                            />
                          ))}
                        </Stack>
                      )
                      : (
                        <Typography variant="caption" color="text.secondary">
                          {t('timetableGenerator.alloc.nobody', { defaultValue: 'nobody' })}
                        </Typography>
                      ))}
                  >
                    {teachers.map((p) => (
                      <MenuItem key={p.key} value={p.key}>
                        {p.name}
                        {p.noAccount && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {t('timetableGenerator.alloc.noAccount', { defaultValue: 'no account yet' })}
                          </Typography>
                        )}
                        {/* WHETHER THEY CAN TAKE IT ON, shown where the choice is
                            made rather than only in a report afterwards. */}
                        <Typography variant="caption" color={p.load > p.capacity ? 'error' : 'text.secondary'} sx={{ ml: 1 }}>
                          {p.load} of {p.capacity}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>

                  {/* HOW MANY PERIODS EACH ONE TAKES.
                      Only worth asking once two people share the subject — with
                      one teacher the answer is always "all of them", and a box
                      saying so is a box to get wrong. Blank means "whatever is
                      left", so a school can set one share and let the other
                      follow. */}
                  {(row.teachers || []).length > 1 && (
                    <ShareEditor row={row} busy={busy} teacherName={teacherName} onSave={onSave} />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={3}>
                <Typography variant="body2" color="text.secondary">
                  {all.length
                    ? t('timetableGenerator.alloc.nonePaid', { defaultValue: 'No subject in this class has periods set yet, so there is nothing to allocate. Set periods on the Subjects tab.' })
                    : t('timetableGenerator.alloc.noSubjects', { defaultValue: 'This class has no subjects allocated. Add them in Class Assignments first.' })}
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        Picking two teachers for one subject splits its periods between them; the generator
        will keep each teacher's share inside their own availability. Assigning somebody also
        gives them their number for that subject — ICT1, ICT2 — which never changes once given,
        so a chart already on the wall stays correct when a fourth teacher is hired.
      </Typography>
    </Stack>
  );
}
